import { AxiosError } from 'axios'
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { usePostApiAuthResetPassword } from '@/shared/apis/fastmile/generated/auth/auth'
import { ApiErrorResponse } from '@/shared/apis/fastmile/models/ApiErrorResponse'
import { LoginCard } from '@/shared/components/LoginCard/LoginCard'
import { StyledAlert, StyledForm, StyledLink, StyledLogin, StyledSubmitButton, StyledTextFieldLabel } from '@/shared/components/LoginCard/StyledLogin'
import { TextField } from '@/shared/components/TextField/TextField'
import { Box, CircularProgress, FormControl, FormLabel, InputAdornment, Typography } from '@mui/material'

export type ResetPasswordPageProps = {
  //
}

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = () => {
  const [token, setToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()

  // Use the generated mutation hook
  const { mutate: resetPassword, isPending: isLoading } = usePostApiAuthResetPassword({
    mutation: {
      onError: (err: AxiosError<ApiErrorResponse>) => {
        if (err.response?.data?.detail) {
          setError(err.response?.data?.detail)
        } else {
          setError(err.message || 'An error occurred while resetting your password.')
        }
      },
      onSuccess: () => {
        setSuccess(true)
      },
    },
  })

  // Extract token from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tokenParam = params.get('token')

    if (tokenParam) {
      setToken(tokenParam)
    } else {
      setError('Invalid or missing reset token. Please try requesting a new password reset link.')
    }
  }, [location.search])

  const onResetPassword = () => {
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')

      return
    }

    // Check password length (requirement from model)
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')

      return
    }

    setError('')

    resetPassword({
      data: {
        token: token,
        newPassword: newPassword,
      },
    })
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onResetPassword()
  }

  const handleNewPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value)
  }

  const handleConfirmPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value)
  }

  const handleLoginClick = () => {
    navigate('/')
  }

  return (
    <StyledLogin>
      <LoginCard>
        {error && <StyledAlert severity="error" sx={{ mb: 3 }}>
          {error}
        </StyledAlert>}
        {success ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Password Reset Successful
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your password has been successfully reset. You can now login with your new password.
            </Typography>
            <StyledSubmitButton
              fullWidth
              size="medium"
              onClick={handleLoginClick}
            >
              Go to Login
            </StyledSubmitButton>
          </Box>
        ) : (
          <StyledForm onSubmit={onSubmit} >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Reset Your Password
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Please enter and confirm your new password.
            </Typography>
            <FormControl required>
              <FormLabel htmlFor="new-password">
                New Password
              </FormLabel>
              <TextField
                id="new-password"
                type="password"
                autoFocus
                autoComplete="new-password"
                aria-describedby="new-password"
                onChange={handleNewPasswordChange}
                value={newPassword}
                fullWidth
              />
            </FormControl>
            <FormControl required>
              <StyledTextFieldLabel htmlFor="confirm-password">
                Confirm Password
              </StyledTextFieldLabel>
              <TextField
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                aria-describedby="confirm-password"
                onChange={handleConfirmPasswordChange}
                value={confirmPassword}
                fullWidth
              />
            </FormControl>
            <StyledSubmitButton
              fullWidth
              size="medium"
              type="submit"
              disabled={isLoading || !token || !newPassword || !confirmPassword}
              loading={isLoading}
              endIcon={isLoading ?
                <InputAdornment position="end">
                  <CircularProgress size="small" />
                </InputAdornment> : undefined
              }
            >
              Reset Password
            </StyledSubmitButton>
            <Box mt={2} textAlign="center">
              <StyledLink to="/">
                Back to Login
              </StyledLink>
            </Box>
          </StyledForm>
        )}
      </LoginCard>
    </StyledLogin>
  )
}